import {CheckIcon, ChevronsUpDown} from "lucide-react";
import * as RPNInput from "react-phone-number-input";

import {Button} from "@/components/ui/button";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command";
import FlagComponent from "@/components/ui/FlagComponent";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {ScrollArea} from "@/components/ui/scroll-area";
import useCountries from "@/hooks/useCountries";
import clsxm from "@/lib/clsxm";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useEvent, useMeasure} from "react-use";

const COUNTRY_SELECTOR_MIN_WIDTH = 300;

type CountrySelectProps = {
  buttonClassName?: string;
  chevronClassName?: string;
  disabled?: boolean;
  error?: string;
  hidePhoneNumber?: boolean;
  onChange: (country: RPNInput.Country) => void;
  showCountryName?: boolean;
  value: RPNInput.Country;
};

const CountrySelect = ({
  buttonClassName,
  chevronClassName,
  disabled,
  error,
  hidePhoneNumber,
  onChange,
  showCountryName,
  value: selectedCountry,
}: CountrySelectProps) => {
  const {t} = useTranslation("company_profile");
  const scrollAreaRef = useRef<HTMLDivElement>(null!);
  const [searchValue, setSearchValue] = useState("");
  const [open, setOpen] = useState(false);
  const {allCountries, getCountryTranslatedLabelByCode} = useCountries();
  const [buttonContainerRef, {width: buttonWidth}] = useMeasure<HTMLDivElement>();

  useEvent("scroll", () => setOpen(false));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div ref={buttonContainerRef} className={clsxm(disabled && "cursor-not-allowed")}>
          <Button
            type="button"
            variant="outline"
            className={clsxm(
              "flex h-14 w-25 gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10",
              error && "border-danger-medium-red focus:border-danger-dark-red",
              disabled && "bg-neutral-white-dark text-neutral-grey-dark",
              buttonClassName,
            )}
            disabled={disabled}>
            <FlagComponent country={selectedCountry ?? "CH"} countryName={selectedCountry ?? "CH"} />
            {showCountryName && (
              <span className="text-base font-medium">{getCountryTranslatedLabelByCode(selectedCountry ?? "CH")}</span>
            )}
            <ChevronsUpDown
              className={clsxm("-mr-2 size-4 opacity-50", disabled ? "hidden" : "opacity-100", chevronClassName)}
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={value => {
              setSearchValue(value);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder={t("search_country")}
          />
          <CommandList>
            <ScrollArea
              ref={scrollAreaRef}
              className="h-72"
              style={{width: Math.max(buttonWidth, COUNTRY_SELECTOR_MIN_WIDTH)}}>
              <CommandEmpty>{t("no_country")}</CommandEmpty>
              <CommandGroup>
                {allCountries?.map(({value, label}) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={country => {
                        onChange(country);

                        setOpen(false);
                      }}
                      hidePhoneNumber={hidePhoneNumber}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  hidePhoneNumber?: boolean;
  onChange: (country: RPNInput.Country) => void;
  selectedCountry: RPNInput.Country;
}

const CountrySelectOption = ({
  country,
  countryName,
  hidePhoneNumber,
  onChange,
  selectedCountry,
}: CountrySelectOptionProps) => {
  return (
    <CommandItem className="gap-2" onSelect={() => onChange(country)}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      {!hidePhoneNumber && (
        <span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      )}
      <CheckIcon className={`ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"}`} />
    </CommandItem>
  );
};

export default CountrySelect;
